/**
 * Toggle Group Component
 * Based on Radix UI Toggle Group primitive
 *
 *   - `tone`: the chosen item wears a tone's fill (`TONE_CLASSES.selected`) —
 *     a hero's choices in its own colour;
 *   - `fit`: each item sized to its words, never clipped to an equal share
 *     ("Every 15 days" stays whole); the group wraps instead.
 */

import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { type VariantProps } from 'class-variance-authority';
import type { Tone } from '@tonnie/design-tokens';
import { cn } from '../lib/utils';
import { TONE_CLASSES } from '../lib/tone-classes';
import { toggleVariants } from './toggle';

interface ToggleGroupLook extends VariantProps<typeof toggleVariants> {
  tone?: Tone;
  fit?: boolean;
}

const ToggleGroupContext = React.createContext<ToggleGroupLook>({
  size: 'default',
  variant: 'default',
});

function ToggleGroup({
  className,
  variant,
  size,
  tone,
  fit,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> & ToggleGroupLook) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      className={cn(
        'group/toggle-group flex w-fit items-center rounded-md data-[variant=outline]:shadow-xs',
        fit && 'flex-wrap',
        className
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size, tone, fit }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}

function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleVariants>) {
  const context = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        'min-w-0 flex-1 shrink-0 rounded-none shadow-none first:rounded-l-md last:rounded-r-md focus:z-10 focus-visible:z-10 data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l',
        context.fit && 'flex-none px-3',
        context.tone && TONE_CLASSES[context.tone].selected,
        className
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
}

export { ToggleGroup, ToggleGroupItem };
